<template>
  <div class="mb-5">
    <template v-if="userHasAccess">
      <template v-if="loaded && !loadError">
        <div class="top-content-bar">
          <div
            v-if="loaded"
            class="wrapper">
            <f-button
              class="text-uppercase"
              theme="go-back"
              withArrow
              withArrowBackwards
              @click.prevent="$router.back()">
              {{ $t('inquiry.backToOffersList') }}
            </f-button>
            <div class="flex full-width inquiry-header">
              <div class="flex flex-align-items-center mr-auto mb-2">
                <img
                  class="mr-2 block my-auto inquiry-img"
                  src="@/assets/images/inquiry-img.png"
                  :alt="$t('inquiry.inquiryImage')">
                <div class="mr-3">
                  <template v-if="isEdit">
                    <h1 class="heading-bigger">
                      <template v-if="showOfferLabel">
                        {{ $t('inquiry.creditOffer') }}
                      </template>
                      <template v-else>
                        {{ $t('inquiry.offerQuestion') }}
                      </template>
                      {{ $t('ui.nr') }} {{ data.id }}</h1>
                    <template v-if="data.adviser_id">
                      <span>{{ $t('inquiry.assignedTo') }} </span><span class="text-light-blue text-medium">{{ data.adviser_name }} {{ data.adviser_surname }}</span>
                    </template>
                    <template v-else>
                      {{ $t('messageForm.noAssignedExpert') }}
                    </template>
                  </template>
                  <template v-else>
                    <h1 class="heading-bigger">
                      <template v-if="showOfferLabel">
                        {{ $t('inquiry.newCreditOffer') }}
                      </template>
                      <template v-else>
                        {{ $t('inquiry.newQuestionOffer') }}
                      </template>
                    </h1>
                  </template>
                </div>
              </div>

              <div class="flex flex-align-items-center flex-wrap mb-2">
                <div class="inquiry-select">
                  <status-select
                    v-if="isEdit"
                    class="mr-1 mb-2-xs inquiry-header-select"
                    :inquiryID="data.id"
                    :status="data.status"
                    :statusAt="data.status_at"
                    :disabled="!data.can_add_message"
                    :isCustomer="userType === 'customer'"/>
                </div>
                <div class="inquiry-buttons">
                  <f-button
                    theme="primary"
                    @click.prevent="$bus.$emit('inquiry-form-submit')">
                    <template v-if="showOfferLabel">
                      {{ $t('inquiry.saveOffer') }}
                    </template>
                    <template v-else>
                      {{ $t('inquiry.saveQuestion') }}
                    </template>
                  </f-button>
                  <f-button
                    v-if="userType === 'customer' && data.can_add_opinion"
                    class="ml-1"
                    theme="warning"
                    @click.prevent="$bus.$emit('inquiry-opinion-popup-show', data.id, data.adviser_name, data.adviser_surname)">
                    {{ $t('inquiry.giveARating') }}
                  </f-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <f-tabs
          ref="inquiry-main-tabs"
          extendedHeader
          :items="tabItems"
          :defaultActiveItem="defaultActiveTab"
          :onToggle="onTabItemToggle">
          <div
            v-if="isEdit"
            slot="tab-header">
            <div class="flex flex-align-items-center ml-auto full-height">
              <f-button
                theme="inverted"
                :disabled="removeInProgress"
                @click="duplicateInquiry"
                class="pl-0-xs">
                <f-icon name="duplicate" />
                <template v-if="showOfferLabel">
                  <span>{{ $t('inquiry.duplicateOffer') }}</span>
                </template>
                <template v-else>
                  <span>{{ $t('inquiry.duplicateQuestion') }}</span>
                </template>
              </f-button>

              <template v-if="isEdit && userType === 'customer'">
                <f-button
                  :theme="isComplaintsMarkedAsread || !data.new_complaint_messages_count ? 'inverted' : 'inverted-danger'"
                  @click="showComplaints"
                  class="ml-2">
                  {{ $t('inquiry.complaints') }}
                </f-button>
              </template>

              <f-button
                class="ml-2"
                theme="danger"
                :disabled="removeInProgress"
                @click="deleteInquiry">
                <template v-if="showOfferLabel">
                  {{ $t('inquiry.deleteOffer') }}
                </template>
                <template v-else>
                  {{ $t('inquiry.deleteQuestion') }}
                </template>
              </f-button>
            </div>
          </div>
          <div
            v-for="(tab, tabIndex) in tabItems"
            :key="'tab-' + tabIndex"
            :slot="'tab-' + tabIndex">
            <template v-if="tab.name === 'inquiry-customer'">
              <inquiry-assigned-customer
                :index="tabIndex"
                ref="inquiry-assigned-customer"
                :readonly="isEdit"
                :isDuplicate="isDuplicate"/>
            </template>
            <template v-else-if="tab.name === 'inquiry-form'">
              <f-alert
                v-if="inquiryReadOnly"
                visible
                class="my-2 my-1-md">
                {{ $t('inquiry.theFormCompletedBy') }}:
                <template v-if="data.created_by && data.created_by === data.adviser_id">
                  {{ data.adviser_name }}  {{ data.adviser_surname }}.
                </template>
                <template v-else>
                  {{ data.customer_name }}  {{ data.customer_surname }}.
                </template>

                <f-button
                  class="ml-auto"
                  theme="text-light"
                  @click="editInquiry">
                  <template v-if="showOfferLabel">
                    <span>{{ $t('inquiry.editOffer') }}</span>
                  </template>
                  <template v-else>
                    <span>{{ $t('inquiry.editQuestion') }}</span>
                  </template>
                </f-button>
              </f-alert>

              <inquiry-form
                ref="inquiry-form"
                :index="tabIndex"
                :readonly="inquiryReadOnly"
                :isDuplicate="isDuplicate" />
            </template>
            <template v-else-if="tab.name === 'inquiry-offers'">
              <!-- <inquiry-files
                :inquiryID="data.id"
                :inquiryFiles="data.files"/> -->
              <inquiry-offers
                :inquiryID="data.id"
                :inquiryPropertyValue="data.property_value"
                :inquiryNeedFinishingAmount="data.need_finishing_amount"
                :inquiryOwnContribution="data.own_contribution"
                :preferedBanks="data.banks"
                :offerLabels="data.offer_labels || []"
                :isReadonly="userType === 'customer'"
                :isTabVisible="offersTabVisible"/>
            </template>
            <template v-else-if="tab.name === 'inquiry-messages'">
              <message-form
                v-if="inquiryMessagesLoaded"
                :inquiryID="data.id"
                :customerName="data.customer_name"
                :customerSurname="data.customer_surname"
                :customerEmail="data.customer_email"
                :customerPhone="data.customer_phone"
                :customerCity="data.customer_city"
                :inquiryAdviser="data.adviser"
                :inquiryMessagesTopicID="inquiryMessages.length ? inquiryMessages[0].id : 0"/>
            </template>
            <template v-else-if="tab.name === 'inquiry-opinion'">
              <inquiry-opinion
                :opinion="data.opinion" />
            </template>
          </div>
        </f-tabs>
        <div
          v-if="isComplaintsVisible"
          class="inquiry-complaints" >
          <template v-if="inquiryComplaintsLoaded">
            <inquiry-complaints
              :inquiryID="data.id"
              :inquiryComplaintsTopicID="inquiryComplaints.length ? inquiryComplaints[0].id : 0" />
          </template>
          <template v-else>
            <div class="loading-spinner-wrapper">
              <div class="loading-spinner pb-3 pt-3">
                <span class="loading-spinner-content">
                  {{ $t('ui.loadingDataInProgress') }}&hellip;
                </span>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template v-else>
        <div
          v-if="!loaded && !loadError"
          class="loading-spinner-wrapper">
          <div class="loading-spinner pb-3 pt-3">
            <span class="loading-spinner-content">
              {{ $t('ui.loadingDataInProgress') }}&hellip;
            </span>
          </div>
        </div>
        <div
          v-if="loadError"
          class="wrapper mt-3">
          <f-alert
            visible
            theme="warning">
            <span>
              {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
            </span>
          </f-alert>
        </div>
      </template>

      <inquiry-opinion-popup />
    </template>
    <div
      v-else
      class="wrapper pt-3 pb-1">
      <f-alert
        visible
        class="my-2"
        theme="inactive">
        <span>
        {{ $t('user.currentPackageDoesNotIncludeAccessToSection') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import InquiryAssignedCustomer from './InquiryAssignedCustomer.vue';
import InquiryForm from './InquiryForm.vue';
import InquiryFiles from './InquiryFiles.vue';
import InquiryOffers from './InquiryOffers.vue';
import MessageForm from '@/views/forms/message/MessageForm.vue';
import StatusSelect from '@/views/forms/inquiry/InquiryStatusSelect.vue';
import InquiryOpinion from './InquiryOpinion.vue';
import InquiryOpinionPopup from '@/components/popups/InquiryOpinionPopup.vue';
import InquiryComplaints from './InquiryComplaints.vue';

export default {
  name: 'inquiry',
  components: {
    'inquiry-assigned-customer': InquiryAssignedCustomer,
    'inquiry-form': InquiryForm,
    'inquiry-files': InquiryFiles,
    'inquiry-offers': InquiryOffers,
    'message-form': MessageForm,
    'status-select': StatusSelect,
    'inquiry-opinion': InquiryOpinion,
    'inquiry-opinion-popup': InquiryOpinionPopup,
    'inquiry-complaints': InquiryComplaints
  },
  computed: {
    userHasAccess () {
      return this.$store.getters.getUserHasFeature('inquiries');
    },
    isEdit () {
      return !!this.$route.params.id;
    },
    tabItems () {
      let inquiryTabItems = [
        {
          title: this.$t('inquiry.creditOffer'),
          titleMobile: this.$t('inquiry.titleMobile'),
          visible: this.isEdit,
          name: 'inquiry-offers',
          triggerAction: true,
          function: this.emitOffersTabVisible,
          order: 1
        },
        {
          title: this.showOfferLabel ? this.$t('inquiry.formOffer') : this.$t('ui.form'),
          titleMobile: this.$t('ui.form'),
          visible: true,
          completed: (this.isEdit || (this.isDuplicate && this.data.customer)) ? 'loading' : false,
          name: 'inquiry-form',
          order: 2
        },
        {
          title: this.$t('inquiry.assignedCustomer'),
          titleMobile: this.$t('client.client'),
          completed: false,
          visible: this.userType === 'adviser',
          name: 'inquiry-customer',
          order: this.isEdit ? 3 : 1
        },
        {
          title: this.$t('ui.messages'),
          visible: this.isEdit && this.data.can_add_message,
          name: 'inquiry-messages',
          triggerAction: true,
          function: this.emitMarkMessagesAsRead,
          order: 4
        },
        {
          title: this.$t('inquiry.opinion'),
          visible: this.data.opinion !== null,
          name: 'inquiry-opinion',
          order: 5
        }
      ];

      return inquiryTabItems.filter(item => item.visible).sort((a, b) => a.order - b.order);
    },
    defaultActiveTab () {
      if (this.newActiveTab) {
        return this.tabItems.indexOf(this.tabItems.find(item => item.name === this.newActiveTab));
      }
      if (!this.$route.meta || !this.$route.meta.activeTab) {
        return 0;
      }

      return this.tabItems.indexOf(this.tabItems.find(item => item.name === this.$route.meta.activeTab));
    },
    showOfferLabel () {
      if (this.$store.getters['getUserType'].toLowerCase() !== 'customer') {
        return true;
      }

      if (!this.isEdit || (this.isEdit && !this.data.files.length)) {
        return false;
      }

      return true
    }
  },
  watch: {
    userHasAccess (newValue) {
      if (newValue && !this.viewInitiated) {
        this.initView();
      }
    }
  },
  data() {
    return {
      data: {
        id: 0,
        created_by: 0,
        adviser: null,
        adviser_id: 0,
        adviser_name: '',
        adviser_surname: '',
        customer: null,
        customer_id: 0,
        customer_name: '',
        customer_surname: '',
        customer_email: '',
        customer_phone: '',
        customer_post_code: '',
        customer_city: '',
        customer_contact_hours: null,
        status: '',
        status_at: '',
        property_value: 0,
        repayment_period: 0,
        own_contribution: 0,
        expected_loan_installment: 0,
        transaction_type_id: 0,
        transaction_subject_id: 0,
        measurement: 0,
        is_need_finishing: false,
        need_finishing_amount: 0,
        transaction_additional_information: '',
        flatmate_count: 1,
        credit_recipient_age: '',
        household_additional_information: '',
        selected_banks: false,
        banks: [],
        liabilities: [],
        incomes: [],
        files: [],
        can_add_message: false,
        can_add_opinion: false,
        opinion: null,
        complaint_messages: null,
        new_complaint_messages_count: null,
        offer_labels: []
      },
      loaded: false,
      loadError: false,
      userType: null,
      inquiryReadOnly: true,
      isDuplicate: false,
      removeInProgress: false,
      inquiryMessages: [],
      inquiryMessagesLoaded: false,
      inquiryComplaints: [],
      inquiryComplaintsLoaded: false,
      inquiryAdviser: {},
      adviserLoaded: false,
      messagesTabIndex: 3,
      newActiveTab: null,
      offersTabVisible: false,
      isComplaintsVisible: false,
      isComplaintsMarkedAsread: false,
      viewInitiated: false
    };
  },
  mounted() {
    this.$bus.$on('inquiry-main-part-complete', this.setFormTypeComplete);
    this.$bus.$on('inquiry-reload', this.loadItemData);
    this.$bus.$on('reset-complaint-messages-count', this.resetNewComplaintMessagesCounter);
    this.userType = this.$store.getters['getUserType'].toLowerCase();

    if (this.userType === 'customer') {
      this.messagesTabIndex = 2;
    }

    if (this.userHasAccess && !this.viewInitiated) {
      this.initView();
    }
  },
  methods: {
    initView () {
      Vue.set(this, 'viewInitiated', true);
      if (this.isEdit) {
        this.loadItemData();
      } else {
        if (this.$route.params && this.$route.params.inquiryData) {
          this.isDuplicate = true;
          this.inquiryReadOnly = false;
          this.loaded = true;
          this.data = this.$route.params.inquiryData;
          this.data.id = 0;
          this.setInquiry();

          return;
        }

        this.inquiryReadOnly = false;
        this.loaded = true;
      }

      Vue.nextTick(() => {
        if (this.tabItems && this.tabItems[0] && this.tabItems[0].triggerAction && this.tabItems[0].function) {
          this.tabItems[0].function();
        }
      })
    },
    loadItemData (activeTab = null, reloadComplains = false) {
      if (reloadComplains) {
        Vue.set(this, 'inquiryComplaintsLoaded', false);
        this.loadComplaints();
      }
      Vue.set(this, 'newActiveTab', activeTab);
      Vue.set(this, 'loaded', false);

      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/api/' + this.userType + '/inquiries/item/',
        instanceFields: [
          'id',
          'created_by',
          'adviser',
          'adviser_id',
          'adviser_name',
          'adviser_surname',
          'customer',
          'customer_id',
          'customer_name',
          'customer_surname',
          'customer_email',
          'customer_phone',
          'customer_post_code',
          'customer_city',
          'customer_contact_hours',
          'status',
          'status_at',
          'property_value',
          'repayment_period',
          'own_contribution',
          'expected_loan_installment',
          'transaction_type_id',
          'transaction_subject_id',
          'measurement',
          'is_need_finishing',
          'need_finishing_amount',
          'transaction_additional_information',
          'flatmate_count',
          'credit_recipient_age',
          'household_additional_information',
          'selected_banks',
          'banks',
          'liabilities',
          'incomes',
          'files',
          'can_add_message',
          'can_add_opinion',
          'opinion',
          'complaint_messages',
          'new_complaint_messages_count',
          'offer_labels'
        ],
        booleanFields: ['is_need_finishing', 'selected_banks', 'can_add_message', 'can_add_opinion'],
        loadedKey: 'loaded',
        successAction: () => {
          Vue.nextTick(() => {
            this.setInquiry();
          });
        }
      });
    },
    duplicateInquiry () {
      this.$router.push({ name: 'Dodawanie oferty kredytowej', params: { inquiryData: this.data } });
    },
    setFormTypeComplete (index, isComplete) {
      this.$refs['inquiry-main-tabs'].setItemCompleted(index, isComplete);
    },
    editInquiry () {
      this.inquiryReadOnly = false;
    },
    setInquiry() {
      Vue.nextTick(() => {
        if (this.userType === 'adviser') {
          let currentCustomer = this.data.customer_email;
          if (this.data.customer && this.data.customer.id) {
            currentCustomer = this.data.customer;
          }

          this.$refs['inquiry-assigned-customer'][0].setCurrentCustomer(currentCustomer, typeof currentCustomer === 'string');
        }

        this.$refs['inquiry-form'][0].setInquiry(this.data);
        this.loadAdditionalData();
      });
    },
    deleteInquiry () {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/' + this.userType + '/inquiries/delete/',
        id: this.data.id,
        successTitle: this.$t('inquiriesList.deleteOffer'),
        successText: this.$t('inquiriesList.selectOfferDelete'),
        confirmTitle: this.$t('inquiriesList.areYouSureDeleteOffer'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('inquiriesList.deleteOfferError'),
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/oferty-kredytowe');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    loadAdditionalData() {
      let where = {};
      where.inquiry_id = this.data.id;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/api/messages/topics/items',
        loadedKey: 'inquiryMessagesLoaded',
        outputKey: 'inquiryMessages',
        pagination: false,
        params: {
          where
        }
      });

      if (this.userType === 'adviser') {
        FormUtils.loadAdditionalData(this, {
          endpoint: '/api/adviser/adviser/item',
          method: 'get',
          outputKey: 'inquiryAdviser',
          loadedKey: 'adviserLoaded',
          errorKey: 'loadError'
        });
      }

      this.loadComplaints();
    },
    loadComplaints () {
      if (this.userType === 'customer' && this.data.id) {
        FormUtils.loadAdditionalData(this, {
          method: 'get',
          endpoint: '/api/customer/complaints/topics/items',
          loadedKey: 'inquiryComplaintsLoaded',
          outputKey: 'inquiryComplaints',
          pagination: false,
          params: {
            where: {
              inquiry_id: this.data.id
            }
          },
          successAction: () => {
            if (this.isComplaintsVisible) {
              Vue.nextTick(() => {
                this.$bus.$emit('mark-complaint-as-read');
              })
            }
          }
        });
      }
    },
    emitOffersTabVisible () {
      Vue.set(this, 'offersTabVisible', true);
      this.$bus.$emit('offers-tab-visible');
    },
    emitMarkMessagesAsRead () {
      this.$bus.$emit('mark-message-as-read');
    },
    resetNewComplaintMessagesCounter () {
      Vue.set(this, 'isComplaintsMarkedAsread', true);
    },
    onTabItemToggle (newActiveTab) {
      if (!newActiveTab) {
        Vue.set(this, 'offersTabVisible', false);
        return;
      }
      Vue.set(this, 'offersTabVisible', newActiveTab.name === 'inquiry-offers');
    },
    showComplaints () {
      this.isComplaintsVisible = true;
      this.$refs['inquiry-main-tabs'].toggle(null);
      Vue.nextTick(() => {
        if (this.inquiryComplaintsLoaded) {
          this.$bus.$emit('mark-complaint-as-read');
        }
      })
    }
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-main-part-complete', this.setFormTypeComplete);
    this.$bus.$off('inquiry-reload', this.loadItemData);
    this.$bus.$off('reset-complaint-messages-count', this.resetNewComplaintMessagesCounter);
  }
};
</script>

<style scoped lang="scss">
.inquiry-header-select {
  width: 320px!important;
}

@import '@/assets/scss/variables.scss';
.inquiry-img {
  height: 67px;
  width: 67px;
}

@media screen and (max-width: $media-tablet-xl) {
  .inquiry-header {
    flex-wrap: wrap;

    & > div {
      width: 100%;
    }


    .inquiry-buttons  {
      margin-left: auto;
    }
  }
}

@media screen and (max-width: $media-phone-l) {
  .inquiry-header {
    .inquiry-buttons {
      display: flex;
      flex-wrap: wrap;
      margin-left: 0;
      margin-top: 2rem;
      width: 100%;

      .f-btn {
        margin-left: auto;
        margin-right: auto;
      }
    }

    & > div {
      .f-btn,
      .f-badge  {
        margin-left: auto;
        margin-right: auto;
      }

      &:last-child {
        align-items: unset!important;
        flex-direction: column;
      }
    }

    .inquiry-header-select {
      max-width: 100%;
      min-width: 100%;
    }
  }
}
</style>
